import { Injectable } from '@angular/core';
import {
  BillingApiService,
  CanCreateSubscriptionsRequest,
  CanCreateSubscriptionsRequestSubscriptionItemInterface,
  CanCreateSubscriptionsResponse,
  ChangeCustomUnitPriceRequest,
  ChangeFrequencyRequest,
  ChangeRenewalDateRequest,
  Commitment as CommitmentApi,
  CreatePricingPlanProductRequest,
  Currency,
  DeletePricingPlanProductRequest,
  FieldMask,
  GenerateVendorReportRequest,
  GetBillableItemRequest,
  GetMultiCustomerSummariesRequest,
  GetMultiProductRequest,
  GetMultiProductResponse,
  GetPricingPlanProductRequest,
  GetPurchaseCostRequest,
  GetPurchaseCostResponse,
  GetRevenueStatsRequest,
  ListBillableItemsRequest,
  ListContractsRequest,
  ListMerchantBillingReportsRequest,
  ListPricingPlanProductsRequest,
  ListProductsRequest,
  ListPurchaseItemsRequestListPurchaseItemsFilters,
  ListPurchaseRequest,
  ListSubscriptionsRequest,
  MerchantReportStatus,
  PagedRequestOptions,
  PauseDunningRequest,
  PricingApiService,
  PurchaseApiService,
  ResumeDunningRequest,
  RetryPurchaseRequest,
  UpdatePricingPlanProductRequest,
  VendorApiService,
  VoidPurchaseRequest,
  ExpireBillableItemRequest,
  CreateSubscriptionRequest,
  SetAutoBillableRequest,
  UpdateSubscriptionRequest,
  UpdateSubscriptionResponse,
  CreateSubscriptionResponse,
} from '@vendasta/billing';
import {
  BillingStrategy,
  billingStrategyFromAPI,
  billingStrategyToAPI,
  ListPricingPlanProductFilter,
  PricingPlanProduct,
  pricingPlanProductFromApi,
} from './pricing';

import { Observable } from 'rxjs';
import { map, mapTo } from 'rxjs/operators';
import { RevenueApiService } from './revenue/revenue.service';
import { MerchantReportApiService } from './merchant-report/merchant-report.service';
import { MerchantReport } from './merchant-report/merchant-report';
import { HttpResponse } from '@angular/common/http';
import { ProductPricing, productPricingFromApi } from './pricing/product-pricing';
import { PagedResponse } from './core/paged-response';
import { Frequency, frequencyToApi } from './pricing/frequency';
import { Commitment } from './pricing/commitment';
import { CurrencyPricingMap, currencyPricingMapToApi } from './pricing/pricing-plan-product';
import { Purchase, purchaseFromApi, PurchaseItem, purchaseItemFromApi } from './purchase/purchase';
import { ListPurchaseFilter } from './purchase/list-purchases';
import { CustomerSummary, customerSummaryFromApi } from './customer_summary/customer-summary';
import { Contract, contractFromApi } from './contract';
import { Product, productFromApi } from './product/product';
import { ListProductsFilter, listProductsFilterToApi } from './product/list-products';
import {
  BillableItem,
  billableItemFromApi,
  ListBillableItemsFilter,
  ListSubscriptionsFilter,
  SubscriptionsProjectionFilter,
} from './billable-item/billable-item';
import { CanCreateSubscriptionsItem } from './subscription';

const isCanCreateSubscriptionsRequestSubscriptionItem = (
  li: CanCreateSubscriptionsRequestSubscriptionItemInterface | undefined,
): li is CanCreateSubscriptionsRequestSubscriptionItemInterface => {
  return !!li;
};
@Injectable({ providedIn: 'root' })
export class BillingService {
  constructor(
    private billingApi: BillingApiService,
    private revenueApi: RevenueApiService,
    private merchantReportApi: MerchantReportApiService,
    private pricingApi: PricingApiService,
    private purchaseApi: PurchaseApiService,
    private vendorApi: VendorApiService,
  ) {}

  /** deprecated */
  getPurchaseCost(merchantId: string, sku: string, quantity: number): Observable<GetPurchaseCostResponse> {
    const req = new GetPurchaseCostRequest({
      merchantId: merchantId,
      sku: sku,
      quantity: quantity,
    });
    return this.billingApi.getPurchaseCost(req).pipe(
      map((response) => {
        if (!response.currency) {
          response.currency = Currency.USD;
        }
        return response;
      }),
    );
  }

  /** deprecated: Use canCreateSubscriptionsWithFrequencies instead. */
  canCreateSubscriptions(
    merchantId: string,
    skus: { [sku: string]: number },
  ): Observable<CanCreateSubscriptionsResponse> {
    const req = new CanCreateSubscriptionsRequest({
      merchantId: merchantId,
      skus: skus,
    });
    return this.billingApi.canCreateSubscriptions(req);
  }

  canCreateSubscriptionsWithFrequencies(
    merchantId: string,
    items: CanCreateSubscriptionsItem[],
  ): Observable<CanCreateSubscriptionsResponse> {
    const req = new CanCreateSubscriptionsRequest({
      merchantId: merchantId,
      items: (items || [])
        .map((item) => {
          if (!item) {
            return undefined;
          }
          return {
            sku: item.sku,
            frequency: frequencyToApi(item.frequency),
            quantity: item.quantity || 0,
          } as CanCreateSubscriptionsRequestSubscriptionItemInterface;
        })
        .filter(isCanCreateSubscriptionsRequestSubscriptionItem),
    });
    return this.billingApi.canCreateSubscriptions(req);
  }

  downloadRevenueCsv(filename: string): Observable<Blob> {
    return this.revenueApi.downloadCsv({ filename });
  }

  downloadBillingReportCsv(merchantId: string, year: number, month: number): Observable<Blob> {
    return this.merchantReportApi.downloadCsv({ merchantId, year, month });
  }

  generateBillingReportCsv(merchantId: string, year: number, month: number): Observable<Blob> {
    return this.merchantReportApi.generateCsv({ merchantId, year, month });
  }

  listMerchantBillingReports(
    merchantId: string,
    cursor: string,
    pageSize: number,
  ): Observable<PagedResponse<MerchantReport>> {
    const req = new ListMerchantBillingReportsRequest({
      merchantId: merchantId,
      pagingOptions: new PagedRequestOptions({ cursor, pageSize }),
    });
    return this.billingApi.listMerchantBillingReports(req).pipe(
      map((resp) => {
        const reports = (resp.reports || []).map((r) => {
          return {
            merchantId: r.merchantId,
            year: r.year,
            month: r.month,
            created: r.created,
            status: MerchantReportStatus[r.status || 0],
          } as MerchantReport;
        });
        return new PagedResponse<MerchantReport>(reports, resp.pagingMetadata.nextCursor, resp.pagingMetadata.hasMore);
      }),
    );
  }

  canBillCreateSnapshot(merchantId: string): Observable<HttpResponse<null>> {
    return this.billingApi.canBillItem({ merchantId: merchantId, sku: 'ST' });
  }

  canBillRefreshSnapshot(merchantId: string): Observable<HttpResponse<null>> {
    return this.billingApi.canBillItem({ merchantId: merchantId, sku: 'snapshotreportrefresh' });
  }

  canBillItem(merchantId: string, sku: string): Observable<boolean> {
    return this.billingApi.canBillItem({ merchantId: merchantId, sku: sku }).pipe(map((response) => response.ok));
  }

  createPricingPlanProduct(
    pricingPlanId: string,
    sku: string,
    frequency: Frequency,
    pricing: CurrencyPricingMap,
    commitment: Commitment,
    strategy: BillingStrategy,
    volumeCommitment: number,
    volumeCommitmentStartsAt?: Date,
    volumeCommitmentEndsAt?: Date,
  ): Observable<boolean> {
    const request = new CreatePricingPlanProductRequest({
      pricingPlanId,
      sku,
      volumeCommitment,
      volumeCommitmentStartsAt,
      volumeCommitmentEndsAt,
    });
    request.frequency = frequencyToApi(frequency);
    request.pricing = currencyPricingMapToApi(pricing);
    request.commitment = new CommitmentApi(commitment);
    request.strategy = billingStrategyToAPI(strategy);
    return this.billingApi.createPricingPlanProduct(request).pipe(mapTo(true));
  }

  updatePricingPlanProduct(
    pricingPlanId: string,
    sku: string,
    {
      frequency,
      pricing,
      commitment,
      strategy,
      volumeCommitment,
      volumeCommitmentStartsAt,
      volumeCommitmentEndsAt,
    }: {
      frequency?: Frequency;
      pricing?: CurrencyPricingMap;
      commitment?: Commitment;
      strategy?: BillingStrategy;
      volumeCommitment?: number;
      volumeCommitmentStartsAt?: Date;
      volumeCommitmentEndsAt?: Date;
    },
  ): Observable<boolean> {
    const fieldMask = new FieldMask({ paths: [] });
    const request = new UpdatePricingPlanProductRequest({ pricingPlanId, sku });

    if (frequency !== null) {
      request.frequency = frequencyToApi(frequency);
      fieldMask.paths.push('frequency');
    }
    if (pricing !== null) {
      request.pricing = currencyPricingMapToApi(pricing);
      fieldMask.paths.push('pricing');
    }
    if (commitment !== null) {
      request.commitment = new CommitmentApi(commitment);
      fieldMask.paths.push('commitment');
    }
    if (strategy !== null) {
      request.strategy = billingStrategyToAPI(strategy);
      fieldMask.paths.push('strategy');
    }
    if (volumeCommitment !== null) {
      request.volumeCommitment = volumeCommitment || 0;
      fieldMask.paths.push('volumeCommitment');
    }
    if (volumeCommitmentStartsAt) {
      request.volumeCommitmentStartsAt = volumeCommitmentStartsAt;
      fieldMask.paths.push('volumeCommitmentStartsAt');
    }
    if (volumeCommitmentEndsAt) {
      request.volumeCommitmentEndsAt = volumeCommitmentEndsAt;
      fieldMask.paths.push('volumeCommitmentEndsAt');
    }
    return this.billingApi.updatePricingPlanProduct(request).pipe(mapTo(true));
  }

  deletePricingPlanProduct(pricingPlanId: string, sku: string): Observable<boolean> {
    const request = new DeletePricingPlanProductRequest({ pricingPlanId, sku });
    return this.billingApi.deletePricingPlanProduct(request).pipe(mapTo(true));
  }

  /** deprecated - Use getMultiWholesalePrices from the WholesalePricingService */
  getMultiProductPricing(
    merchantId: string,
    skus: string[],
    pricingPlanId?: string,
  ): Observable<{ [sku: string]: ProductPricing }> {
    return this.billingApi.getMultiProductPricing({ merchantId, skus, pricingPlanId }).pipe(
      map((r) => {
        const productPricesApi = r.productPrices || {};
        const productPricings: { [key: string]: ProductPricing } = {};
        for (const k of Object.keys(productPricesApi)) {
          const productPricingResponse = productPricesApi[k];
          productPricings[k] = productPricingFromApi(productPricingResponse);
        }
        return productPricings;
      }),
    );
  }

  /** deprecated - Use getMultiRetailPrices from the RetailPricingService */
  getMultiRetailPricing(
    merchantId: string,
    currency: Currency,
    skus: string[],
    groupId?: string,
  ): Observable<{ [sku: string]: ProductPricing }> {
    const requestGroupId: string = groupId === undefined ? '' : groupId;
    return this.pricingApi.getMultiRetail({ merchantId, currency, skus, groupId: requestGroupId }).pipe(
      map((r) => {
        const retailPricesApi = r.productPrices || {};
        const retailPricings: { [key: string]: ProductPricing } = {};
        for (const k of Object.keys(retailPricesApi)) {
          const retailPricingResponse = retailPricesApi[k];
          retailPricings[k] = productPricingFromApi(retailPricingResponse);
        }
        return retailPricings;
      }),
    );
  }

  listPricingPlanProducts(
    filters: ListPricingPlanProductFilter,
    cursor: string,
    pageSize: number,
  ): Observable<PagedResponse<PricingPlanProduct>> {
    const req = new ListPricingPlanProductsRequest({
      filters: filters,
      pagingOptions: new PagedRequestOptions({ cursor, pageSize }),
    });
    return this.billingApi.listPricingPlanProducts(req).pipe(
      map((response) => {
        const products = (response.products || []).map((p) => pricingPlanProductFromApi(p));
        return new PagedResponse(products, response.pagingMetadata.nextCursor, response.pagingMetadata.hasMore);
      }),
    );
  }

  getPricingPlanProduct(pricingPlanId: string, sku: string): Observable<PricingPlanProduct> {
    const req = new GetPricingPlanProductRequest({ pricingPlanId, sku });
    return this.billingApi.getPricingPlanProduct(req).pipe(
      map((resp) => {
        const ppp: PricingPlanProduct = pricingPlanProductFromApi(resp.pricingPlanProduct);
        ppp.defaultStrategy = billingStrategyFromAPI(resp.pricingPlanDefaultStrategy);
        return ppp;
      }),
    );
  }

  listPurchases(filters: ListPurchaseFilter, cursor: string, pageSize: number): Observable<PagedResponse<Purchase>> {
    const req = new ListPurchaseRequest({
      filters: filters,
      pagingOptions: new PagedRequestOptions({ cursor, pageSize }),
    });
    return this.purchaseApi.list(req).pipe(
      map((response) => {
        const purchases = (response.purchases || []).map((purchase) => purchaseFromApi(purchase));
        return new PagedResponse(purchases, response.pagingMetadata.nextCursor, response.pagingMetadata.hasMore);
      }),
    );
  }

  voidPurchase(merchantId: string, purchaseId: string, day: string): Observable<boolean> {
    const req = new VoidPurchaseRequest({ purchaseId, merchantId, day });
    return this.purchaseApi.void(req).pipe(mapTo(true));
  }

  listPurchaseItems(
    filters: ListPurchaseItemsRequestListPurchaseItemsFilters,
    cursor: string,
    pageSize: number,
  ): Observable<PagedResponse<PurchaseItem>> {
    const req = new ListPurchaseRequest({
      filters: filters,
      pagingOptions: new PagedRequestOptions({ cursor, pageSize }),
    });
    return this.purchaseApi.listPurchaseItems(req).pipe(
      map((response) => {
        const purchaseItems = (response.purchaseItems || []).map((purchaseItem) =>
          purchaseItemFromApi(purchaseItem, response.currency),
        );
        return new PagedResponse(purchaseItems, response.pagingMetadata.nextCursor, response.pagingMetadata.hasMore);
      }),
    );
  }

  retryPurchase(merchantId: string, day: string, purchaseId: string): Observable<boolean> {
    const req = new RetryPurchaseRequest({
      merchantId: merchantId,
      day: day,
      purchaseId: purchaseId,
    });
    return this.purchaseApi.retry(req).pipe(map(() => true));
  }

  getMultiCustomerSummaries(
    merchantId: string,
    customerIds: string[],
    periodStart: Date,
    periodEnd: Date,
  ): Observable<CustomerSummary[]> {
    const req = new GetMultiCustomerSummariesRequest({
      customerIds: customerIds,
      merchantId: merchantId,
      periodStart: periodStart,
      periodEnd: periodEnd,
    });
    return this.purchaseApi.getMultiCustomerSummaries(req).pipe(
      map((response) => {
        return (response.customerSummaries || []).map((customerSummary) => customerSummaryFromApi(customerSummary));
      }),
    );
  }

  getMultiContract(merchantIds: string[]): Observable<{ [sku: string]: Contract }> {
    return this.billingApi.getMultiContract({ merchantIds: merchantIds }).pipe(
      map((resp) => {
        const contractsAPI = resp.contracts || {};
        const contracts: { [key: string]: Contract } = {};
        for (const k of Object.keys(contractsAPI)) {
          const contractResponse = contractsAPI[k];
          contracts[k] = contractFromApi(contractResponse);
        }
        return contracts;
      }),
    );
  }

  getMerchantWholesaleCurrency(merchantId: string): Observable<string> {
    return this.billingApi.getMerchantWholesaleCurrency({ merchantId: merchantId }).pipe(
      map((resp) => {
        return Currency[resp.currency] || 'USD';
      }),
    );
  }

  generateVendorReport(
    skus: string[],
    periodStart: Date,
    periodEnd: Date,
    vendorName: string,
    markAsApproved: boolean,
  ): Observable<string> {
    const req = new GenerateVendorReportRequest({
      skus: skus,
      periodStart: periodStart,
      periodEnd: periodEnd,
      vendorName: vendorName,
      markAsApproved: markAsApproved,
    });
    return this.vendorApi.generateReport(req).pipe(map((response) => response.url));
  }

  getVendorStats(skus: string[], periodStart: Date, periodEnd: Date): Observable<number> {
    const req = new GetRevenueStatsRequest({
      skus: skus,
      periodStart: periodStart,
      periodEnd: periodEnd,
    });
    return this.vendorApi.getRevenueStats(req).pipe(map((response) => response.totalRevenue || 0));
  }

  listContracts(cursor: string, pageSize: number): Observable<PagedResponse<Contract>> {
    const req = new ListContractsRequest({
      pagingOptions: new PagedRequestOptions({ cursor, pageSize }),
    });
    return this.billingApi.listContracts(req).pipe(
      map((response) => {
        const contracts = (response.contracts || []).map((c) => contractFromApi(c));
        return new PagedResponse(contracts, response.pagingMetadata.nextCursor, response.pagingMetadata.hasMore);
      }),
    );
  }

  listProducts(
    merchantId: string,
    searchTerm: string,
    filters: ListProductsFilter,
    cursor: string,
    pageSize: number,
  ): Observable<PagedResponse<Product>> {
    const apiFilters = listProductsFilterToApi(filters);
    const req = new ListProductsRequest({
      merchantId,
      searchTerm,
      filters: apiFilters,
      pagingOptions: new PagedRequestOptions({ cursor, pageSize }),
    });
    return this.billingApi.listProducts(req).pipe(
      map((response) => {
        const products = (response.products || []).map((p) => productFromApi(p));
        return new PagedResponse(products, response.pagingMetadata.nextCursor, response.pagingMetadata.hasMore);
      }),
    );
  }

  getMultiProduct(skus: string[]): Observable<Map<string, Product>> {
    const req = new GetMultiProductRequest({
      skus: skus,
    });
    return this.billingApi.getMultiProduct(req).pipe(
      map((response: GetMultiProductResponse) => {
        const productsMap = new Map<string, Product>();
        skus.forEach((sku) => {
          productsMap.set(sku, productFromApi(response.products[sku]));
        });
        return productsMap;
      }),
    );
  }

  getBillableItem(
    merchantId: string,
    sku: string,
    customerId: string,
    orderId: string,
    projectionFilter?: SubscriptionsProjectionFilter,
  ): Observable<BillableItem> {
    const req = new GetBillableItemRequest({
      merchantId,
      sku,
      customerId,
      orderId,
      projectionFilter,
    });

    return this.billingApi.getBillableItem(req).pipe(map((response) => billableItemFromApi(response.item)));
  }

  expireSubscription(
    merchantId: string,
    sku: string,
    customerId: string,
    orderId: string,
    expiry: Date,
  ): Observable<HttpResponse<null>> {
    const req = new ExpireBillableItemRequest({
      merchantId,
      sku,
      customerId,
      orderId,
      expiry,
    });
    return this.billingApi.expireBillableItem(req);
  }

  unexpireSubscription(
    merchantId: string,
    sku: string,
    customerId: string,
    orderId: string,
  ): Observable<HttpResponse<null>> {
    const req = new ExpireBillableItemRequest({
      merchantId,
      sku,
      customerId,
      orderId,
    });
    return this.billingApi.unexpireBillableItem(req);
  }

  changeBillableItemFrequency(
    merchantId: string,
    sku: string,
    customerId: string,
    orderId: string,
    freq: Frequency,
  ): Observable<HttpResponse<null>> {
    const req = new ChangeFrequencyRequest({
      merchantId: merchantId,
      sku: sku,
      customerId: customerId,
      orderId: orderId,
      frequency: frequencyToApi(freq),
    });

    return this.billingApi.changeFrequency(req);
  }

  changeBillableItemNextRenewalDate(
    merchantId: string,
    sku: string,
    customerId: string,
    orderId: string,
    next: Date,
  ): Observable<HttpResponse<null>> {
    const req = new ChangeRenewalDateRequest({
      merchantId: merchantId,
      sku: sku,
      customerId: customerId,
      orderId: orderId,
      nextRenewalDate: next,
    });

    return this.billingApi.changeRenewalDate(req);
  }

  changeBillableItemCustomUnitPrice(
    merchantId: string,
    sku: string,
    customerId: string,
    orderId: string,
    amount: number,
  ): Observable<HttpResponse<null>> {
    const req = new ChangeCustomUnitPriceRequest({
      merchantId: merchantId,
      sku: sku,
      customerId: customerId,
      orderId: orderId,
      customUnitPrice: amount,
    });

    return this.billingApi.changeCustomUnitPrice(req);
  }

  //deprecated: use listSubscriptions
  listBillableItems(
    searchTerm: string,
    filters: ListBillableItemsFilter,
    cursor: string,
    pageSize: number,
  ): Observable<PagedResponse<BillableItem>> {
    const req = new ListBillableItemsRequest({
      searchTerm: searchTerm,
      filters: filters,
      pagingOptions: new PagedRequestOptions({ cursor, pageSize }),
    });
    return this.billingApi.listBillableItems(req).pipe(
      map((response) => {
        const items = (response.items || []).map((item) => billableItemFromApi(item));
        return new PagedResponse(items, response.pagingMetadata.nextCursor, response.pagingMetadata.hasMore);
      }),
    );
  }

  listSubscriptions(
    filters: ListSubscriptionsFilter,
    cursor: string,
    pageSize: number,
    projectionFilter?: SubscriptionsProjectionFilter,
  ): Observable<PagedResponse<BillableItem>> {
    const req = new ListSubscriptionsRequest({
      filters: filters,
      pagingOptions: new PagedRequestOptions({ cursor, pageSize }),
      projectionFilter: projectionFilter,
    });
    return this.billingApi.listSubscriptions(req).pipe(
      map((response) => {
        const items = (response.subscriptions || []).map((item) => billableItemFromApi(item));
        return new PagedResponse(items, response.pagingMetadata.nextCursor, response.pagingMetadata.hasMore);
      }),
    );
  }

  listRetailSubscriptions(
    filters: ListSubscriptionsFilter,
    cursor: string,
    pageSize: number,
    projectionFilter?: SubscriptionsProjectionFilter,
  ): Observable<PagedResponse<BillableItem>> {
    const req = new ListSubscriptionsRequest({
      filters: filters,
      pagingOptions: new PagedRequestOptions({ cursor, pageSize }),
      projectionFilter: projectionFilter,
    });
    return this.billingApi.listRetailSubscriptions(req).pipe(
      map((response) => {
        const items = (response.subscriptions || []).map((item) => billableItemFromApi(item));
        return new PagedResponse(items, response.pagingMetadata.nextCursor, response.pagingMetadata.hasMore);
      }),
    );
  }

  startRevRecBackfillWorkflow(): Observable<HttpResponse<null>> {
    return this.billingApi.startRevRecTemplateBackfill({});
  }

  pauseDunning(merchantId: string, purchaseId: string, day: string, pauseDunningUntil: Date): Observable<boolean> {
    const req = new PauseDunningRequest({ merchantId, purchaseId, day, pauseDunningUntil });
    return this.purchaseApi.pauseDunning(req).pipe(map((response) => response.ok));
  }

  resumeDunning(merchantId: string, purchaseId: string, day: string): Observable<boolean> {
    const req = new ResumeDunningRequest({ merchantId, purchaseId, day });
    return this.purchaseApi.resumeDunning(req).pipe(map((response) => response.ok));
  }

  exportSubscriptions(filters: ListSubscriptionsFilter): Observable<null> {
    const req = new ListSubscriptionsRequest({
      filters: filters,
    });
    return this.billingApi.exportSubscriptions(req).pipe(map(() => null));
  }

  createSubscription(req: CreateSubscriptionRequest): Observable<CreateSubscriptionResponse> {
    return this.billingApi.createSubscription(req);
  }

  setAutoBillable(subscriptionId: string, autoBillable: boolean): Observable<HttpResponse<null>> {
    const req = new SetAutoBillableRequest({
      subscriptionId: subscriptionId,
      autoBillable: autoBillable,
    });
    return this.billingApi.setAutoBillable(req);
  }

  updateSubscription(subscription: BillableItem, fieldMask: FieldMask): Observable<UpdateSubscriptionResponse> {
    const req = new UpdateSubscriptionRequest({
      subscription: {
        subscriptionId: subscription.subscriptionId,
        description: subscription.description,
      },
      fieldMask: fieldMask,
    });
    return this.billingApi.updateSubscription(req);
  }
}
